import { Result, ok, err } from "neverthrow";
import { ParseError } from "../../shared/errors/parseError";
import {
  IVocDetailParser,
  HttpVocDetail,
  HttpFile,
  HttpTalkReply,
} from "../bbsRepository.types";
import dayjs from "dayjs";
import { injectable } from "inversify";
import { IFile } from "@/domain/entities/bbs/qnaDetail.types";
import { IVocDetail, IVocReply } from "@/domain/entities/bbs";

function parseFile(data: HttpFile): IFile {
  const file: IFile = {
    fileUid: data.fileUid,
    trxUid: data.trxUid,
    fileTypeCode: data.fileTypeCode,
    fileTypeName: data.fileTypeName,
    fileName: data.fileName,
    fileUrl: data.fileUrl,
    savedTs: data.savedTs,
  };
  return file;
}

export function parseVocDetail(data: HttpVocDetail): IVocDetail {
  const vocDetail = {
    uid: data.uid,
    allocUid: data.allocUid,
    serviceUid: data.serviceUid,
    title: data.title,
    vocTypeCode: data.vocTypeCode,
    vocTypeName: data.vocTypeName,
    boardDtm: data.boardDtm
      ? dayjs(data.boardDtm).format("YYYY-MM-DD HH:mm")
      : "",
    lineName: data.lineName,
    lineRouteNo: data.lineRouteNo,
    lineRouteName: data.lineRouteName,
    bodyText: data.bodyText,
    displayYn: data.displayYn,
    secretYn: data.secretYn,
    statusCode: data.statusCode,
    statusName: data.statusName,
    statusTs: data.statusTs,
    files: data.files.map(parseFile),
    vocReplies: data.vocReplies?.map(parseVocReply) ?? [],
    writerUid: data.createdUid,
    updatedTs: dayjs(data.updatedTs).format("YYYY-MM-DD HH:mm"),
    createdTs: dayjs(data.createdTs).format("YYYY-MM-DD HH:mm"),
    savedTs: dayjs(data.savedTs).format("YYYY-MM-DD HH:mm"),
  };
  return vocDetail;
}

function parseVocReply(httpTalkReply: HttpTalkReply): IVocReply {
  const talkReply: IVocReply = {
    title: httpTalkReply.title,
    bodyText: httpTalkReply.bodyText,
    createdTs: dayjs(httpTalkReply.createdTs).format("YYYY-MM-DD HH:mm:ss"),
    savedTs: dayjs(httpTalkReply.savedTs).format("YYYY-MM-DD HH:mm:ss"),
  };

  return talkReply;
}

function parseDeletedFile(deletedFile: IFile): HttpFile {
  return {
    fileUid: deletedFile.fileUid,
  };
}

function toHttpFile(file: IFile | null): HttpFile {
  const httpFile: IFile = file?.fileUid
    ? { fileUid: file.fileUid }
    : { fileName: file?.fileName };

  return httpFile;
}

function parseToUTC(dateTime?: string) {
  return dateTime ? dayjs(dateTime).format() : undefined;
}

@injectable()
export class VocDetailParser implements IVocDetailParser {
  constructor() {}

  toDomain(data: HttpVocDetail): Result<IVocDetail, ParseError> {
    try {
      const vocDetail = parseVocDetail(data);
      return ok(vocDetail);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }

  fromDomain(data: IVocDetail): HttpVocDetail {
    const httpVocDetail: HttpVocDetail = {
      uid: data.uid,
      allocUid: data.allocUid ? data.allocUid : undefined,
      serviceUid: data.serviceUid,
      title: data.title,
      vocTypeCode: data.vocTypeCode,
      vocTypeName: data.vocTypeName,
      boardDtm: parseToUTC(data.boardDtm),
      lineName: data.lineName ? data.lineName : undefined,
      bodyText: data.bodyText,
      displayYn: data.displayYn,
      secretYn: data.secretYn,
      files:
        data.files
          ?.map(toHttpFile)
          .filter((file) => !!file.fileName || !!file.fileUid) ?? [],
      deletedFiles: data.deletedFiles?.map(parseDeletedFile) ?? [],
      vocReplies: data.vocReplies?.map(parseVocReply) ?? [],
      createdUid: data.writerUid,
      statusCode: data.statusCode,
      statusName: data.statusName,
      statusTs: parseToUTC(data.statusTs),
    };
    return httpVocDetail;
  }
}
